import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { supabaseHelpers } from "@/lib/supabase";
import React, { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { Toaster, toast } from "react-hot-toast";
import { useLocation } from "wouter";
import WavyCircle from "./circle";
import { LanguageDialog } from "@/components/ui/LanguageDialog";
import { LANGUAGES, getLanguageByCode } from "@/lib/languages";
import { AuthUpgradeModal } from "@/components/modals/AuthUpgradeModal";

// All theme colors now come from CSS custom properties via the palette system.
// See index.css for palette definitions.

// --- Top Loading Bar ---
interface TopLoadingBarProps {
    isLoading: boolean;
}

const TopLoadingBar: React.FC<TopLoadingBarProps> = ({ isLoading }) => {
    const barClasses = isLoading ? "h-1 bg-[var(--accent)] transition-all duration-300 ease-in-out" : "h-1 bg-transparent";

    return (
        <div className="fixed top-0 left-0 w-full z-50 overflow-hidden">
            <div
                className={barClasses}
                style={{
                    width: isLoading ? "100%" : "0%",
                    transition: isLoading ? "width 0.3s linear" : "none",
                }}
            />
        </div>
    );
};

// --- Dark Mode Hook ---
//
// Single source of truth for the welcome-flow theme. Multiple components in the
// flow call useDarkMode() (header toggle on each step, plus parent containers).
// Previously each call had its own useState, so toggling in one component
// updated localStorage but the other component's stale state effect would
// re-apply the old theme to <html> on its next render — causing the chosen
// theme to "snap back" when the user advanced steps.

const themeStore = (() => {
    let listeners: Array<() => void> = [];
    let current: "light" | "dark" = (() => {
        if (typeof window === "undefined") return "light";
        const saved = localStorage.getItem("theme");
        if (saved === "light" || saved === "dark") return saved;
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    })();

    return {
        get: () => current,
        subscribe: (cb: () => void) => {
            listeners.push(cb);
            return () => {
                listeners = listeners.filter((l) => l !== cb);
            };
        },
        toggle: () => {
            current = current === "dark" ? "light" : "dark";
            try { localStorage.setItem("theme", current); } catch {}
            listeners.forEach((l) => l());
        },
    };
})();

const useDarkMode = () => {
    const theme = useSyncExternalStore(
        themeStore.subscribe,
        themeStore.get,
        themeStore.get,
    );

    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove("light", "dark");
        root.classList.add(theme);
    }, [theme]);

    return { theme, toggleTheme: themeStore.toggle };
};

// const useDarkMode = () => {
//     const [theme, setTheme] = useState<"light" | "dark">(() => {
//         const savedTheme = localStorage.getItem("theme");
//         return savedTheme === "light" || savedTheme === "dark" ? savedTheme : "dark";
//     });

//      toggconstleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"));

//     useEffect(() => {
//         const root = window.document.documentElement;
//         localStorage.setItem("theme", theme);

//         if (theme === "dark") {
//             root.classList.add("dark");
//             root.classList.remove("light");
//         } else {
//             root.classList.remove("dark");
//             root.classList.add("light");
//         }
//     }, [theme]);

//     return { theme, toggleTheme };
// };

// --- Navigation Footer ---
interface NavigationProps {
    currentStep: number;
    totalSteps: number;
    onPrevious: () => void;
    onNext: () => void;
    onComplete: () => void;
    isLoading: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ currentStep, totalSteps, onPrevious, onNext, onComplete, isLoading }) => {
    const isFirstStep = currentStep === 1;
    const isLastStep = currentStep === totalSteps;

    return (
        // The parent is min-h-dvh, so this row sits flush at the bottom of the
        // viewport — which on a notched phone is the home-indicator zone. Pad
        // by the safe-area inset or Next/Complete lands under it.
        <div className="flex justify-between items-center w-full px-4 sm:px-6 lg:px-10 py-3 sm:py-5 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] sm:pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))]">
            <div className="flex-1 text-left">
                {!isFirstStep && (
                    <button onClick={onPrevious} disabled={isLoading} className={`dark:text-white text-gray-800 text-base font-medium transition py-2 ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"}`}>
                        &larr; Previous
                    </button>
                )}
            </div>

            <div className="flex-1 text-center text-base text-gray-500">
                {currentStep} of {totalSteps}
            </div>

            <div className="flex-1 text-right">
                {isLastStep ? (
                    <button onClick={onComplete} disabled={isLoading} className={`text-[var(--accent)] text-base font-medium transition py-2 ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"}`}>
                        Complete &rarr;
                    </button>
                ) : (
                    <button onClick={onNext} disabled={isLoading} className={`dark:text-white text-gray-800 text-base font-medium transition py-2 ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"}`}>
                        Next &rarr;
                    </button>
                )}
            </div>
        </div>
    );
};

// --- Dots Indicator ---
interface DotsIndicatorProps {
    currentStep: number;
    totalSteps: number;
}

const DotsIndicator: React.FC<DotsIndicatorProps> = ({ currentStep, totalSteps }) => (
    <div className="flex space-x-2">
        {Array.from({ length: totalSteps }).map((_, index) => (
            <div key={index} className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${index + 1 === currentStep ? "dark:bg-white bg-gray-800" : "dark:bg-gray-700 bg-gray-300"}`} />
        ))}
    </div>
);

// --- Theme Toggle ---
interface ThemeToggleProps {
    theme: "light" | "dark" | "system";
    toggleTheme: () => void;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ theme, toggleTheme }) => {
    const isSystemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const effectiveTheme = theme === "system" ? (isSystemDark ? "dark" : "light") : theme;
    
    return (
        <button onClick={toggleTheme} className="text-xl text-gray-500 cursor-pointer transition p-2 rounded-full" aria-label="Toggle dark/light mode">
            {effectiveTheme === "dark" ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="white">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-black" fill="none" viewBox="0 0 24 24" stroke="black">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
            )}
        </button>
    );
};

// --- Translation Demo ---
// Shows Vyzle's core USP: context-aware translation that understands meaning, not just words.
const TRANSLATION_EXAMPLES = [
    { original: '"That test was a piece of cake"', translated: "Ese examen fue muy fácil", lang: "Spanish" },
    { original: '"I\'m feeling under the weather"', translated: "Je ne me sens pas bien", lang: "French" },
    { original: '"Let\'s break the ice"', translated: "Lass uns das Eis brechen", lang: "German" },
    { original: '"It\'s raining cats and dogs"', translated: "Il pleut des cordes", lang: "French" },
];

const TranslationShowcase: React.FC = () => {
    const [index, setIndex] = useState(0);
    const [phase, setPhase] = useState<"original" | "translating" | "translated">("original");

    useEffect(() => {
        const cycle = () => {
            setPhase("original");
            const t1 = setTimeout(() => setPhase("translating"), 1200);
            const t2 = setTimeout(() => setPhase("translated"), 2000);
            const t3 = setTimeout(() => {
                setIndex((prev) => (prev + 1) % TRANSLATION_EXAMPLES.length);
            }, 4500);
            return [t1, t2, t3];
        };

        const timers = cycle();
        const interval = setInterval(() => {
            timers.forEach(clearTimeout);
            timers.length = 0;
            timers.push(...cycle());
        }, 5000);

        return () => {
            clearInterval(interval);
            timers.forEach(clearTimeout);
        };
    }, []);

    const example = TRANSLATION_EXAMPLES[index];

    return (
        <div className="w-full max-w-sm sm:max-w-md mt-4 sm:mt-6">
            <div className="relative rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white/60 dark:bg-white/5 backdrop-blur-sm p-4 overflow-hidden">
                {/* Subtle accent line on top */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[var(--bubble-own-from)] to-[var(--bubble-own-to)]" />

                <div className="flex flex-col gap-2.5">
                    {/* Original message */}
                    <div className="flex items-start gap-2">
                        <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 shrink-0">EN</span>
                        <p className="text-sm sm:text-base text-gray-700 dark:text-gray-200 italic">
                            {example.original}
                        </p>
                    </div>

                    {/* Arrow + translation */}
                    <div className={`flex items-start gap-2 transition-all duration-500 ${
                        phase === "original" ? "opacity-0 translate-y-1" : "opacity-100 translate-y-0"
                    }`}>
                        <span className="text-xs text-[var(--accent)] mt-0.5 shrink-0">{example.lang.substring(0, 2).toUpperCase()}</span>
                        {phase === "translating" ? (
                            <span className="text-sm text-gray-400 dark:text-gray-500">translating...</span>
                        ) : (
                            <p className="text-sm sm:text-base font-medium text-[var(--accent)]">
                                {example.translated}
                            </p>
                        )}
                    </div>
                </div>

                {/* Label */}
                <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 mt-3 text-center">
                    Context-aware — understands idioms, not just words
                </p>
            </div>
        </div>
    );
};

// --- Feature Pills ---
const FEATURES = [
    { icon: "🌍", label: "80+ Languages" },
    { icon: "✨", label: "No Sign-up" },
    { icon: "🗣️", label: "Multilingual Groups" },
];

const FeaturePills: React.FC = () => (
    <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-4 sm:mt-5">
        {FEATURES.map((f, i) => (
            <div
                key={f.label}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm
                    bg-white/60 dark:bg-white/5 border border-gray-200 dark:border-gray-700/50
                    text-gray-700 dark:text-gray-300 backdrop-blur-sm
                    animate-in fade-in slide-in-from-bottom-2"
                style={{ animationDelay: `${i * 120}ms`, animationFillMode: "both", animationDuration: "500ms" }}
            >
                <span>{f.icon}</span>
                <span>{f.label}</span>
            </div>
        ))}
    </div>
);

// --- Step Content Components ---
interface Step1IdentityProps {
    userName: string;
    setUserName: (name: string) => void;
    pronouns: string;
    setPronouns: (p: string) => void;
    onComplete?: () => void;
    onSignInClick?: () => void;
}

// Keep this list short on purpose — least-intrusive onboarding. "" means "prefer not to say".
const PRONOUN_OPTIONS: { value: string; label: string }[] = [
    { value: "she/her", label: "She/Her" },
    { value: "he/him", label: "He/Him" },
    { value: "they/them", label: "They/Them" },
    { value: "", label: "Skip" },
];

const Step1Welcome: React.FC<Step1IdentityProps> = ({ userName, setUserName, pronouns, setPronouns, onComplete, onSignInClick }) => (
    <div className="flex flex-col items-center justify-center text-center w-full max-w-xs sm:max-w-md lg:max-w-xl flex-grow px-4">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-light mb-1 dark:text-white text-gray-900">Welcome to Vyzle</h1>
        <p className="text-base sm:text-lg md:text-xl font-extralight mb-2 sm:mb-0 mt-2 sm:mt-3 dark:text-gray-300 text-gray-600">
            Chat across languages, instantly
        </p>

        <TranslationShowcase />
        <FeaturePills />
<p className="text-sm sm:text-base font-light mt-5 dark:text-gray-400 text-gray-500">Pick a name and start chatting in seconds</p>
            {!!localStorage.getItem("pending_invite_code") && (
                <p className="text-sm font-medium mt-2 text-[var(--accent)]">You've been invited to a chat — you'll join it right after setup.</p>
            )}
        <div className="w-full mt-6 sm:mt-3 flex flex-col items-center">
            <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Enter your name / nickname"
                /* text-base (16px) minimum: iOS auto-zooms on focus below that */
                className="w-full sm:w-4/5 md:w-3/5 p-3 sm:p-4 text-base sm:text-lg md:text-xl rounded-lg border bg-white dark:bg-[rgb(10,19,19)] text-gray-900 dark:text-white border-[var(--accent)] dark:border-gray-700 text-center placeholder:text-sm sm:placeholder:text-sm md:placeholder:text-base focus:outline-none focus:ring-0 focus:border-gray-800 dark:focus:border-[var(--accent)] transition"
                onKeyDown={(e) => {
                    if (e.key === "Enter" && onComplete) {
                        e.preventDefault();
                        onComplete();
                    }
                }}
                autoFocus
            />
            {/* Pronoun chips — small, optional. Helps translation into gendered languages. */}
            <div className="w-full sm:w-full md:w-full lg:w-full mt-3 flex flex-wrap items-center justify-center gap-1.5">
                <span className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mr-1">
                    Pronouns (optional):
                </span>
                {PRONOUN_OPTIONS.map((p) => {
                    const isSelected = pronouns === p.value;
                    return (
                        <button
                            key={p.value || "skip"}
                            type="button"
                            onClick={() => setPronouns(p.value)}
                            className={`px-2.5 py-1 text-[11px] sm:text-xs rounded-full border transition
                                ${isSelected
                                    ? "bg-gradient-to-r from-[var(--bubble-own-from)] to-[var(--bubble-own-to)] text-white border-transparent"
                                    : "bg-white/60 dark:bg-white/5 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-[var(--accent)]"
                                }`}
                            aria-pressed={isSelected}
                        >
                            {p.label}
                        </button>
                    );
                })}
            </div>
            <p className="text-[10px] sm:text-xs font-light mt-2 dark:text-gray-500 text-gray-400">
                Helps translate your messages accurately in gendered languages
            </p>
            
            {onSignInClick && (
                <button
                    type="button"
                    onClick={onSignInClick}
                    className="mt-3 py-2 px-3 text-sm font-bold text-[var(--accent)] underline-offset-4 hover:underline focus:outline-none focus-visible:underline"
                >
                    Already have an account? Sign in
                </button>
            )}
        </div>
    </div>
);

// --- Step 3 Language ---
interface Step2LanguageProps {
    selectedLanguage: string | null;
    setSelectedLanguage: (lang: string) => void;
    onComplete?: (langCode?: string) => void;
    userName?: string;
}

const Step2Language: React.FC<Step2LanguageProps> = ({ selectedLanguage, setSelectedLanguage, onComplete, userName }) => {
    // ... (rest of state and effects)
    const { theme } = useDarkMode();
    const [topLanguages, setTopLanguages] = useState<string[]>([]);
    const [showLanguageSelector, setShowLanguageSelector] = useState(false);
    const [isLoadingLanguages, setIsLoadingLanguages] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [selectedFromMore, setSelectedFromMore] = useState(false);
    const [, navigate] = useLocation();

    // Detect languages
    useEffect(() => {
        if (hasLoaded) return;

        const fetchLocationAndSetLanguages = async () => {
            setIsLoadingLanguages(true);

            // Default fallback
            const browserLangCode = navigator.language.split("-")[0];
            const defaultLangs = ["en", "es", "hi"];

            // Map country/regions to Language CODES
            const enhancedCountryMap: Record<string, string[]> = {
                IN: ["hi", "en", "bn", "te", "ta", "mr", "gu", "kn", "ml", "pa"],
                US: ["en", "es"],
                CA: ["en", "fr"],
                FR: ["fr", "en"],
                ES: ["es", "en"],
                DE: ["de", "en"],
                IT: ["it", "en"],
                CN: ["zh", "en"],
                JP: ["ja", "en"],
                KR: ["ko", "en"],
                BR: ["pt-BR", "en"],
                RU: ["ru", "en"],
                EG: ["ar", "en"],
                TR: ["tr", "en"],
                NL: ["nl", "en"],
                SE: ["sv", "en"],
                VN: ["vi", "en"],
                TH: ["th", "en"],
                GR: ["el", "en"],
                IL: ["he", "en"],
                PL: ["pl", "en"],
                ID: ["id", "en"],
                MY: ["ms", "en"],
                PH: ["tl", "en"],
                BD: ["bn", "en"],
                PK: ["ur", "en"],
                NG: ["en", "ha", "yo", "ig"],
                ZA: ["en", "af", "zu"],
                KE: ["sw", "en"],
                ET: ["am", "en"],
                SA: ["ar", "en"],
                AE: ["ar", "en"],
            };

            const regionLanguageMap: Record<string, string[]> = {
                // Map regions if we had granular data, using codes
                // Example placeholders
                Maharashtra: ["mr", "hi", "en"],
                Karnataka: ["kn", "hi", "en"],
                Gujarat: ["gu", "hi", "en"],
                "Tamil Nadu": ["ta", "en", "hi"],
                Telangana: ["te", "hi", "en"],
                Kerala: ["ml", "en", "hi"],
                "West Bengal": ["bn", "hi", "en"],
                Punjab: ["pa", "hi", "en"],
                Odisha: ["or", "hi", "en"],
                Assam: ["as", "hi", "en"],
            };

            try {
                let data;
                try {
                    const controller = new AbortController();
                    setTimeout(() => controller.abort(), 3000);
                    const res = await fetch("https://ipapi.co/json/", { signal: controller.signal });
                    data = await res.json();
                } catch {
                    // Same 3s cap as the primary — a hung fallback used to leave
                    // the language step stuck on "Detecting language…" forever
                    const controller = new AbortController();
                    setTimeout(() => controller.abort(), 3000);
                    const res = await fetch("https://api.country.is/", { signal: controller.signal });
                    const fallbackData = await res.json();
                    data = { country: fallbackData.country, region: null };
                }

                const countryCode = data.country;
                const region = data.region;

                const countryLanguages = enhancedCountryMap[countryCode] || ["en"];
                const regionalLanguages = (region && regionLanguageMap[region]) || [];

                // Use Set to unique-ify codes
                // Prioritize: Regional, Browser, Country, then Global defaults
                const combined = Array.from(new Set([
                    ...(regionalLanguages.length ? regionalLanguages : []),
                    browserLangCode,
                    ...countryLanguages,
                    "en", "es", "fr", "hi"
                ]));

                // Filter to ensure we only include supported languages
                const supportedCodes = combined.filter(code => getLanguageByCode(code));

                setTopLanguages(supportedCodes.slice(0, 4));

                // Select first one if nothing selected
                if (!selectedLanguage) setSelectedLanguage(supportedCodes[0] || "en");
            } catch (error) {
                setTopLanguages(["en", "es", "hi"]);
                if (!selectedLanguage) setSelectedLanguage("en");
            } finally {
                setIsLoadingLanguages(false);
                setHasLoaded(true);
            }
        };

        fetchLocationAndSetLanguages();
    }, [hasLoaded]);

    const handleLanguageSelection = async (langCode: string, fromMoreLanguages: boolean = false) => {
        console.log("Language selected:", langCode);
        setSelectedLanguage(langCode);
        setSelectedFromMore(fromMoreLanguages);

        if (onComplete) {
            onComplete(langCode);
        }
    };

    const SELECTED_GRADIENT_CLASSES = `
    text-white font-semibold 
    shadow-[0_4px_25px_rgba(16,185,129,0.7)] 
    bg-gradient-to-r 
    from-[var(--bubble-own-from)] 
    to-[var(--bubble-own-to)] 
  `;

    const DEFAULT_CLASSES = `
    bg-white dark:bg-[rgb(10,19,19)]
    border-gray-400 dark:border-gray-700 
    text-gray-900 dark:text-white 
    hover:border-gray-700 hover:dark:border-[var(--accent)]
  `;

    const LanguageButton = ({ langCode, onClick }: { langCode: string; onClick: () => void }) => {
        const langInfo = getLanguageByCode(langCode);
        const displayName = langInfo ? langInfo.name : langCode;

        return (
            <button onClick={onClick} className={`w-full flex justify-center items-center p-3 sm:p-4 text-sm sm:text-base md:text-lg rounded-lg border transition duration-200 ${selectedLanguage === langCode ? SELECTED_GRADIENT_CLASSES : DEFAULT_CLASSES}`}>
                {displayName}
            </button>
        );
    };

    return (
        <div className="flex flex-col items-center text-center w-full max-w-sm sm:max-w-2xl md:max-w-4xl lg:max-w-5xl flex-grow pt-4 sm:pt-8 md:pt-0 px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light mb-2 dark:text-white text-gray-900">Select your language</h1>
            <p className="text-base sm:text-lg md:text-xl font-extralight mb-6 sm:mb-6 mt-3 sm:mt-2 dark:text-gray-300 text-gray-600">We'll handle the rest</p>

            {isLoadingLanguages ? (
                <div className="w-full max-w-sm sm:max-w-md md:max-w-lg flex justify-center items-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Detecting language…</span>
                </div>
            ) : (
                <div className="w-full max-w-sm sm:max-w-md md:max-w-lg grid grid-cols-1 gap-3 mb-4">
                    {topLanguages.map((langCode) => (
                        <LanguageButton key={langCode} langCode={langCode} onClick={() => handleLanguageSelection(langCode, false)} />
                    ))}

                    {/* ✅ Show selected language box ONLY if from modal */}
                    {selectedLanguage && selectedFromMore && !topLanguages.includes(selectedLanguage) && (
                        <div
                            className={`w-full flex justify-center items-center p-3 text-sm sm:text-base md:text-lg rounded-lg border transition duration-200
      ${SELECTED_GRADIENT_CLASSES}`}
                        >
                            <span className="ml-2">{getLanguageByCode(selectedLanguage)?.name || selectedLanguage}</span>
                        </div>
                    )}

                    <button
                        onClick={() => setShowLanguageSelector(true)}
                        className={`w-full flex justify-center items-center p-3 text-sm sm:text-base md:text-lg rounded-lg border transition duration-200
              ${showLanguageSelector ? SELECTED_GRADIENT_CLASSES : DEFAULT_CLASSES}`}
                    >
                        More Languages...
                    </button>
                </div>
            )}

            {/* Dialog */}
            <LanguageDialog
                isOpen={showLanguageSelector}
                onClose={() => setShowLanguageSelector(false)}
                onLanguageSelect={(langCode) => handleLanguageSelection(langCode, true)}
                selectedLanguage={selectedLanguage || null}
            />

            <p className="text-sm text-gray-500">Seamless translation across all conversations</p>
        </div>
    );
};

export default Step2Language;

// --- Main WelcomeFlow Component ---
interface WelcomeFlowProps {
    onComplete: (data: { language: string; name: string }) => void;
    isFirebaseConfigured: boolean;
}

export const WelcomeFlow: React.FC<WelcomeFlowProps> = ({ onComplete, isFirebaseConfigured }) => {
    // ... (rest of the component logic remains the same)

    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [userName, setUserName] = useState("");
    const [pronouns, setPronouns] = useState<string>(() => localStorage.getItem("vyzle-chat-pronouns") || "");
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
    const { user, signIn } = useAuth();
    const [, navigate] = useLocation();

    const totalSteps = 2;
    const { theme, toggleTheme } = useDarkMode();

    const simulateLoading = (callback: () => void) => {
        if (isLoading) return;

        setIsLoading(true);
        setTimeout(() => {
            callback();
            setIsLoading(false);
        }, 300);
    };

    // Guard against double completion: picking a language auto-completes,
    // but the "Complete →" button stays clickable while signIn/profile sync
    // are in flight. A second run used to find pending_invite_code already
    // consumed and redirect to /app/chat, losing the invite.
    const completingRef = useRef(false);

    const handleComplete = async (overrideLang?: string) => {
        if (completingRef.current) return;
        completingRef.current = true;
        try {
            const finalLang = (typeof overrideLang === 'string' ? overrideLang : selectedLanguage) || "";

            // Save preferences immediately (before auth) so useAuth can pick them up
            localStorage.setItem("vyzle-chat-onboarded", "true");
            localStorage.setItem("vyzle-chat-language", finalLang);
            localStorage.setItem("vyzle-chat-user-name", userName);
            if (pronouns) {
                localStorage.setItem("vyzle-chat-pronouns", pronouns);
            } else {
                localStorage.removeItem("vyzle-chat-pronouns");
            }

            // Sign in user if not already signed in. Use the returned user:
            // the `user` from the hook closure is still null right after
            // signIn(), which used to skip this profile sync for new users.
            const activeUser = user ?? (await signIn());

            if (activeUser?.uid) {
                // Update user data in Supabase
                try {
                    await supabaseHelpers.updateUser(activeUser.uid, {
                        display_name: userName,
                        preferred_language: finalLang || undefined,
                        pronouns: pronouns || null,
                    });
                    console.log("User preferences saved to Supabase");
                } catch (error) {
                    console.warn("Failed to save preferences to Supabase:", error);
                    // Continue anyway - localStorage is already updated
                }
            }

            // toast({
            //   title: "Welcome to Vyzle Chat!",
            //   description: "You're all set up and ready to connect with everyone worldwide.",
            // });

            toast.success("Welcome to Vyzle Chat!");

            // Redirect to pending invite if one exists, otherwise go to chat
            // Use replace() so the back button doesn't return to onboarding
            const pendingInvite = localStorage.getItem("pending_invite_code");
            if (pendingInvite) {
                localStorage.removeItem("pending_invite_code");
                window.location.replace(`/app/join/${pendingInvite}`);
            } else {
                window.location.replace("/app/chat");
            }
        } catch (error) {
            completingRef.current = false;
            console.error("Onboarding completion error:", error);
            toast.error("Setup Error ! There was an issue completing your setup. Please try again.");

            // toast({
            //   title: "Setup Error",
            //   description: "There was an issue completing your setup. Please try again.",
            //   variant: "destructive",
            // });
        }
    };

    const handleNext = () => {
        simulateLoading(() => {
            // Step 1 validation: User name
            if (step === 1 && userName.trim() === "") {
                toast.error("Please enter your name to continue.");
                return; // Stop navigation
            }

            // Step 2 validation: Language selection
            if (step === 2 && !selectedLanguage) {
                toast.error("Please select a language to continue.");
                return;
            }

            if (step === totalSteps) {
                // Last step: call onComplete
                console.log("Completing with:", { language: selectedLanguage, name: userName.trim() });
                onComplete({
                    language: selectedLanguage || "",
                    name: userName.trim(),
                });
                handleComplete();
            } else {
                // Otherwise, go to next step
                setStep((prev) => Math.min(prev + 1, totalSteps));
            }
        });
    };

    const handlePrevious = () => {
        simulateLoading(() => setStep((prev) => Math.max(prev - 1, 1)));
    };

    const renderStepContent = () => {
        switch (step) {
            case 1:
                return (
                    <Step1Welcome
                        userName={userName}
                        setUserName={setUserName}
                        pronouns={pronouns}
                        setPronouns={setPronouns}
                        onComplete={handleNext}
                        onSignInClick={() => setShowLoginModal(true)}
                    />
                );
            case 2:
                return <Step2Language selectedLanguage={selectedLanguage} setSelectedLanguage={setSelectedLanguage} onComplete={handleComplete} userName={userName} />;
            default:
                return <div>Unknown Step</div>;
        }
    };

    return (
        <div
            className={`min-h-dvh flex flex-col items-center font-sans transition-colors duration-500
        dark:text-white text-gray-900 bg-[var(--chat-bg)]
      `}
        >
            <TopLoadingBar isLoading={isLoading} />
            <div className="flex justify-between items-center w-full px-4 sm:px-6 lg:px-10 py-2 sm:py-3">
                <DotsIndicator currentStep={step} totalSteps={totalSteps} />
                <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
            </div>
            <div className="flex flex-col items-center flex-grow w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-6xl px-4 sm:px-6">{renderStepContent()}</div>
            <Navigation
                currentStep={step}
                totalSteps={totalSteps}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onComplete={handleNext} // handleNext calls onComplete automatically on last step
                isLoading={isLoading}
            />
            <Toaster position="top-right" /> {/* Toast notifications */}
            <TopLoadingBar isLoading={isLoading} />
            {/* Sign in with existing account — magic link, no password.
                Uses loginOnly so an unknown email shows a "no account found"
                state instead of creating an orphan upgrade link. */}
            <AuthUpgradeModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                currentUser={null}
                loginOnly
            />
        </div>
    );
};
